import {
  File as GraphQLFile,
  useTourPoiQuery,
  useUpdateContentSectionMutation,
  useUploadFileMutation,
  useCreateContentSectionMutation,
  useDeleteContentSectionMutation
} from '@typings/graphql'
import { Reference } from '@apollo/client'
import { apolloClient } from '@services/apollo'
import React, { useEffect, useState, useRef } from 'react'
import { useForm, FormProvider, Controller } from 'react-hook-form'
import { useNavigate, useParams } from 'react-router'
import { Box, TextField, Typography, Button, FormControlLabel, Checkbox } from '@mui/material'
import HexColorInput from '@shared/components/ui/HexColorInput'
import { useTranslation } from 'react-i18next'
import FileDragDrop from '@features/cms/components/FileDragDrop'
import { trimHexColor } from '@utils/color'
import { convertToTextObject } from '@utils/texts'
import { FormFooterBar } from '@features/cms/components/ui/FormFooterBar'
import DeleteIcon from '@mui/icons-material/Delete'
import { ConfirmDialog } from '@features/cms/components/dialogs/ConfirmDialog'
import { FileList } from '@features/cms/components/FileList'
import { isGraphQlFile } from '@features/cms/components/content-type-editors/mutation-helper/file'
import { useUnsavedChangesAlert } from '@hooks/useUnsavedChangesAlert'
import { uploadDummyKeyvisual } from '@utils/dummyImage'

type UpdateSectionFormInput = {
  title: string,
  bgColor: string,
  cardColor: string,
  keyvisual: File | GraphQLFile | null | undefined,
  inDemo: boolean,
  hidden: boolean
}

const PoiEditSection: React.FC = () => {
  const { id, modeId, poiId, sectionId } = useParams()
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [showSnackbar, setShowSnackbar] = useState(false)
  const [showConfirmDialog, setShowConfirmDialog] = useState(false)
  const [showColorSyncDialog, setShowColorSyncDialog] = useState(false)
  const [isSyncingColors, setIsSyncingColors] = useState(false)
  const pendingColors = useRef<{ bgColor: string, cardColor: string } | null>(null)

  const [createSection, { loading: isCreating, error: errorInCreate }] = useCreateContentSectionMutation()
  const [updateSection, { loading: isUpdating, error: errorInUpdate }] = useUpdateContentSectionMutation()
  const [uploadKeyvisual, { loading: isUploading, error: uploadError }] = useUploadFileMutation()
  const [deleteSection] = useDeleteContentSectionMutation()

  const { data, refetch } = useTourPoiQuery({ variables: { id: poiId as string } })
  const poi = data?.tourPoi

  const section = poi?.sections.find((s) => s.id === sectionId)

  const methods = useForm<UpdateSectionFormInput>({
    defaultValues: {
      title: section?.title ?? t('edit.poi.newSection'),
      bgColor: section?.config.bgColor ?? '#000',
      cardColor: section?.config.cardColor ?? '#fff',
      keyvisual: section?.files.find((f) => f.key === 'keyvisual'),
      inDemo: !!section?.inDemo,
      hidden: section?.hidden ?? false
    }
  })

  useUnsavedChangesAlert(methods.formState.isDirty)

  useEffect(() => {
    if (!section) return

    methods.reset({
      title: section.title,
      bgColor: section.config.bgColor,
      cardColor: section.config.cardColor,
      keyvisual: section.files.find((f) => f.key === 'keyvisual'),
      inDemo: section.inDemo,
      hidden: section.hidden
    })
  }, [section])

  const onSubmit = async (submittedData: UpdateSectionFormInput) => {
    // Clear the dirty baseline up front so the post-create navigate isn't caught by the
    // leave-guard (the awaited create/update lets React commit isDirty=false first).
    methods.reset(submittedData)

    const translatableTexts = [convertToTextObject('title', submittedData.title)]

    let modelId = sectionId

    if (section) {
      try {
        await updateSection({
          variables: {
            id: section.id,
            data: {
              texts: translatableTexts,
              poiId: poi!.id,
              order: section.order,
              demo: submittedData.inDemo,
              config: {
                cardColor: submittedData.cardColor,
                bgColor: submittedData.bgColor
              },
              hidden: submittedData.hidden
            }
          }
        })

        const colorsChanged =
          submittedData.bgColor !== section.config.bgColor ||
          submittedData.cardColor !== section.config.cardColor
        const siblings = poi!.sections.filter((s) => s.id !== sectionId)

        if (colorsChanged && siblings.length > 0) {
          pendingColors.current = { bgColor: submittedData.bgColor, cardColor: submittedData.cardColor }
          methods.reset(submittedData)
          setShowColorSyncDialog(true)
          return
        }
      } catch (e) {}
    } else {
      try {
        const result = await createSection({
          variables: {
            data: {
              texts: translatableTexts,
              poiId: poi!.id,
              order: poi!.sections.length,
              demo: submittedData.inDemo,
              config: {
                cardColor: submittedData.cardColor,
                bgColor: submittedData.bgColor
              }
            }
          }
        })

        modelId = result.data?.createContentSection.id

        // Add section to poi.sections via cache (no network query that could
        // overwrite the optimistic dummy before backend finishes processing)
        if (modelId) {
          apolloClient.cache.modify({
            id: apolloClient.cache.identify({ __typename: 'TourPoi', id: poiId }),
            fields: {
              sections(existing = [], { toReference }) {
                const ref = toReference({ __typename: 'ContentSection', id: modelId })
                if (!ref || existing.some((s: Reference) => s.__ref === ref.__ref)) return existing
                return [...existing, ref]
              }
            }
          })
        }

        // Upload dummy: cache.modify appends it to the section's files
        if (modelId && !submittedData.keyvisual) {
          await uploadDummyKeyvisual('ContentSection', modelId).catch(console.warn)
        }

        navigate(`/tour/${id}/mode/${modeId}/poi/${poi!.id}/section/${modelId}`)
      } catch (e) {}
    }

    if (submittedData.keyvisual && !isGraphQlFile(submittedData.keyvisual) && modelId) {
      try {
        await uploadKeyvisual({
          variables: {
            file: submittedData.keyvisual,
            data: {
              key: 'keyvisual',
              replace: true,
              model: 'ContentSection',
              modelId
            }
          }
        })
      } catch (e) {}
    }

    methods.reset(submittedData)
    setShowSnackbar(true)
  }

  const onConfirmDelete = async () => {
    try {
      await deleteSection({ variables: { id: sectionId! } })
      navigate(`/tour/${id}/mode/${modeId}/poi/${poi!.id}/sections`)
    } catch (e) {
      console.log(e)
    }
  }

  const onConfirmColorSync = async () => {
    if (!pendingColors.current || !poi) return
    setIsSyncingColors(true)
    const siblings = poi.sections.filter((s) => s.id !== sectionId)
    await Promise.all(
      siblings.map((sibling) =>
        updateSection({
          variables: {
            id: sibling.id,
            data: {
              texts: [convertToTextObject('title', sibling.title)],
              poiId: poi.id,
              order: sibling.order,
              demo: sibling.inDemo,
              config: {
                bgColor: pendingColors.current!.bgColor,
                cardColor: pendingColors.current!.cardColor
              },
              hidden: sibling.hidden
            }
          }
        })
      )
    )
    setIsSyncingColors(false)
    setShowColorSyncDialog(false)
    setShowSnackbar(true)
  }

  const onCancelColorSync = () => {
    setShowColorSyncDialog(false)
    setShowSnackbar(true)
  }

  const closeSnackbar = () => {
    setShowSnackbar(false)
    methods.reset()
  }

  return <FormProvider {...methods} >
    <form style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}
      onSubmit={methods.handleSubmit(onSubmit)}
    >
      <Box
        flex={1}
        p={4}
        sx={{
          overflowY: 'auto'
        }}
      >

        <Box display="flex">
          <Box display="flex" flexDirection="column" flex="0 0 50%" paddingRight="32px" gap={4}>
            <TextField
              label={t('common.title')}
              id="title"
              {...methods.register('title')}
            />
            <Controller
              name="bgColor"
              control={methods.control}
              render={({ field }) => (
                <HexColorInput
                  label={t('common.bgColor')}
                  id="bgColor"
                  value={field.value}
                  onChange={(v) => field.onChange(trimHexColor(v))}
                />
              )}
            />
            <Controller
              name="cardColor"
              control={methods.control}
              render={({ field }) => (
                <HexColorInput
                  label={t('common.cardColor')}
                  id="cardColor"
                  value={field.value}
                  onChange={(v) => field.onChange(trimHexColor(v))}
                />
              )}
            />
            <Controller
              name="inDemo"
              control={methods.control}
              render={({ field: { onChange, value } }) => <FormControlLabel
                control={<Checkbox sx={{ py: 0 }} checked={value} onChange={e => onChange(e.currentTarget.checked)} />}
                label={t('common.demo')}
              />}
            />
            <Controller
              name="hidden"
              control={methods.control}
              render={({ field: { onChange, value } }) => <FormControlLabel
                control={<Checkbox sx={{ py: 0 }} checked={value} onChange={e => onChange(e.currentTarget.checked)} />}
                label={t('common.hide')}
              />}
            />
          </Box>
          <Box flex="0 0 50%">
            <Controller
              name="keyvisual"
              control={methods.control}
              render={({ field: { onChange, value } }) => <FileDragDrop
                accept={{
                  'image/*': ['.png', '.jpg', '.jpeg', '.webp']
                }}
                preview
                initialFile={value as GraphQLFile}
                onFileChanged={onChange}
                placeholderSrc="/dummy-image.png"
              >
                {!section?.keyvisual &&
                <Typography>{t('edit.poi.dragImage')}</Typography>
                }
              </FileDragDrop>}
            />
            {sectionId && <Box mt={4}>
              <FileList
                files={section?.files as any ?? []}
                model="ContentSection"
                modelId={sectionId}
                refetch={refetch}
              />
            </Box>}
          </Box>
        </Box>
      </Box>

      <FormFooterBar
        disabled={isUpdating || isCreating || isUploading || isSyncingColors}
        loading={isUpdating || isCreating || isUploading || isSyncingColors}
        uploadError={errorInCreate || errorInUpdate || uploadError}
        showSnackbar={showSnackbar}
        closeSnackbar={closeSnackbar}
      >
        {section &&
          <Button
            color="warning"
            variant="outlined"
            endIcon={<DeleteIcon />}
            onClick={() => setShowConfirmDialog(true)}
          >
            {t('edit.poi.deleteSection')}
          </Button>}
      </FormFooterBar>
      <ConfirmDialog
        open={showConfirmDialog}
        text={t('edit.poi.confirmDeleteSection')}
        onConfirm={onConfirmDelete}
        onCancel={() => setShowConfirmDialog(false)}
      />
      <ConfirmDialog
        open={showColorSyncDialog}
        text={t('edit.poi.applyColorsToSiblings')}
        confirmLabel={t('common.apply')}
        cancelLabel={t('edit.poi.doNotApply')}
        onConfirm={onConfirmColorSync}
        onCancel={onCancelColorSync}
      />

    </form>
  </FormProvider>
}

export default PoiEditSection
